import { useEffect, useRef, useState } from "react";
import type { Lang } from "../lib/i18n";
import type { Theme } from "../lib/state";
import { pick } from "../lib/i18n";
import type { UserProfile } from "../lib/auth";

interface AppHeaderProps {
  lang: Lang;
  theme: Theme;
  profile?: UserProfile | null;
  onToggleLang: () => void;
  onToggleTheme: () => void;
  onSignOut?: () => void;
}

export function AppHeader({ lang, theme, profile, onToggleLang, onToggleTheme, onSignOut }: AppHeaderProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const initial = (profile?.name || "?")[0];

  return (
    <header className="kd-header">
      <div className="kd-header-inner" dir="ltr">
        <div className="kd-header-brand">
          <img src={theme === "dark" ? "/logo_dark.svg" : "/logo_light.svg"} alt="Kaden Investment" className="kd-header-logo" />
        </div>
        <div className="kd-header-actions">
          <label className="kd-lang-switch" title={lang === "ar" ? "Switch to English" : "التبديل للعربية"}>
            <input type="checkbox" checked={lang === "en"} onChange={onToggleLang} aria-label="Switch language" />
            <span>عربي</span>
            <span>English</span>
          </label>
          <label className="theme" title={theme === "dark" ? "Light mode" : "Dark mode"}>
            <span className="theme__toggle-wrap">
              <input type="checkbox" className="theme__toggle" checked={theme === "dark"} onChange={onToggleTheme} aria-label="Toggle theme" />
              <span className="theme__icon" aria-hidden="true">
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
                <span className="theme__icon-part" />
              </span>
            </span>
          </label>
          {profile?.name && (
            <div className="kd-header-user" ref={menuRef}>
              <button className="kd-header-user-btn" onClick={() => setMenuOpen((o) => !o)} aria-haspopup="menu" aria-expanded={menuOpen} title={profile.name}>
                {profile.photo ? (
                  <img src={profile.photo} alt={profile.name} className="kd-header-avatar" />
                ) : (
                  <span className="kd-header-avatar kd-header-avatar-fallback">{initial}</span>
                )}
                <span className="kd-header-user-name">{profile.name.split(" ")[0]}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {menuOpen && (
                <div className="kd-header-menu" role="menu" dir={lang === "ar" ? "rtl" : "ltr"}>
                  <div className="kd-header-menu-header">
                    <strong>{profile.name}</strong>
                    {profile.role && <small>{profile.role}</small>}
                    {profile.email && <small className="kd-header-menu-email">{profile.email}</small>}
                  </div>
                  {onSignOut && (
                    <button
                      className="kd-header-menu-item kd-header-menu-danger"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onSignOut();
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      <span>{t("تسجيل خروج", "Sign out")}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
